import { createFeatureSelector, createSelector, MemoizedSelector } from '@ngrx/store';
import moment from 'moment';
import { ConfigConstants } from '../../../config/config-constants';
import { NOTIFICATION_STATUS_CODE } from '../../../enums/notification-status-code.enum';
import { StatusModel } from '../../../models/low-level/status.model';
import { Equipment } from '../../../types/equipment';
import { NotificationStatus } from '../../../types/notification-status';
import { isDefined } from '../../../utilities/is-defined';
import { TimeUtilities } from '../../../utilities/time-utilities';
import { LoadStatus } from '../../load-status';
import { AddressState } from '../../address';
import { selectAddresses } from '../../address/selectors/address.selector';
import { selectEnableSafetyAssessmentForm } from '../../config/selectors/config.selector';
import type { NotificationTaskState } from '../notification-entities.state';
import {
    affectedEquipmentOf,
    completionKeyOf,
    customerNameOf,
    equipmentToState,
    latestTaskForCode,
    mainEquipmentOf,
    NotificationAddresses,
    resolveNotificationAddresses,
    statusRecordToStatus,
    statusStateToModel,
    taskPlannedEnd,
    taskPlannedStart,
} from '../notification-collection.converters';
import { durationRecordForArrival } from '../notification-status.rules';
import {
    createDefaultNotificationStatus,
    NOTIFICATION_FEATURE_KEY,
    NotificationCollectionState,
    NotificationCompletionsState,
    NotificationCompletionState,
    NotificationLoadMarker,
    NotificationPropertiesSnapshot,
    NotificationState,
    NotificationStatusRecordState,
} from '../notification-collection.state';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import {
    EntryStatusPair,
    NextStatusEntry,
    NotificationCardState,
    NotificationDetailState,
    NotificationNavigationEntry,
} from '../notification.types';

// ---- model-free business logic ----------------------------------------------------------------
// These operate on a single entry — its postprocessed fields and the statuses recorded on it — never
// on class instances, so everything they need is already in the store. They are the shared core
// both the per-id selectors and the bulk (today/upcoming/...) selectors below are built from.
//
// Most stay file-private. The ones that are exported are shared with `NotificationEffects`, which
// has to derive the same values for *freshly-fetched* entries that are not in the store yet (see
// its `init$`) and therefore cannot go through a selector for them.

const IN_PROGRESS_CODES = new Set<string>([
    NOTIFICATION_STATUS_CODE.OSR,
    NOTIFICATION_STATUS_CODE.TRAV,
    NOTIFICATION_STATUS_CODE.ROSR,
    NOTIFICATION_STATUS_CODE.FCC,
]);
const TO_BE_SIGNED_CODES = new Set<string>([
    NOTIFICATION_STATUS_CODE.TOBS,
    NOTIFICATION_STATUS_CODE.RTOBS,
    NOTIFICATION_STATUS_CODE.FTOBS,
]);
/** The status codes that mark a notification as cancelled. */
const CANCELLED_STATUS_CODES = new Set<string>([
    NOTIFICATION_STATUS_CODE.AVR1,
    NOTIFICATION_STATUS_CODE.AVR2,
    NOTIFICATION_STATUS_CODE.CANC,
]);
const OSR_OR_HIGHER_CODES = new Set<string>([
    NOTIFICATION_STATUS_CODE.OSR,
    NOTIFICATION_STATUS_CODE.SOSR,
    NOTIFICATION_STATUS_CODE.TOBS,
    NOTIFICATION_STATUS_CODE.ROSR,
    NOTIFICATION_STATUS_CODE.FCC,
    NOTIFICATION_STATUS_CODE.RTOBS,
    NOTIFICATION_STATUS_CODE.FTOBS,
    NOTIFICATION_STATUS_CODE.CRM,
    NOTIFICATION_STATUS_CODE.SCRM,
]);

/**
 * `true` for the codes that mean work on the notification has started: on site (OSR), travelling
 * there (TRAV) or working it remotely (ROSR/FCC).
 *
 * @param code
 */
export function isInProgressCode(code: string): boolean {
    return IN_PROGRESS_CODES.has(code);
}

/**
 * `true` for the codes that mean the work is done and the service report awaits the customer's
 * signature.
 *
 * @param code
 */
export function isToBeSignedCode(code: string): boolean {
    return TO_BE_SIGNED_CODES.has(code);
}

/**
 * `true` while the notification is neither new nor closed: being worked on, or waiting for its
 * signature.
 *
 * @param code
 */
export function isInProgressOrToBeSignedCode(code: string): boolean {
    return isInProgressCode(code) || isToBeSignedCode(code);
}

/**
 * `true` for the codes that mark a notification as cancelled.
 *
 * @param code
 */
export function isCancelledStatusCode(code: string): boolean {
    return CANCELLED_STATUS_CODES.has(code);
}

/**
 * `true` once the notification is closed for good: confirmed (CRM), or its cancellation confirmed
 * (CNCL).
 *
 * @param code
 */
export function isConfirmedOrCancellationConfirmedCode(code: string): boolean {
    return code === NOTIFICATION_STATUS_CODE.CRM || code === NOTIFICATION_STATUS_CODE.CNCL;
}

/**
 * `true` for the codes that mean the notification is worked remotely: full remote support (FCC) or
 * on-site repair reported remotely (ROSR), and their to-be-signed counterparts (FTOBS/RTOBS).
 *
 * Exported for `deriveStatusWorkEntries` (see the timesheet derivation), which needs the same test
 * to decide whether a notification's status-derived timesheet rows book under the remote-work codes.
 *
 * @param code
 */
export function isRemoteWorkCode(code: string): boolean {
    return (
        code === NOTIFICATION_STATUS_CODE.FCC ||
        code === NOTIFICATION_STATUS_CODE.FTOBS ||
        code === NOTIFICATION_STATUS_CODE.ROSR ||
        code === NOTIFICATION_STATUS_CODE.RTOBS
    );
}

/**
 * The taskSet-derived status for `code`, in the plain `NotificationStatus` shape.
 *
 * @param entry
 * @param code
 */
function statusFromTaskSet(entry: NotificationState, code: string): NotificationStatus | undefined {
    const task = latestTaskForCode(entry.tasks, code);
    if (!task) {
        return undefined;
    }
    return {
        statusCode: code as NOTIFICATION_STATUS_CODE,
        startTime: taskPlannedStart(task).toISOString(),
        endTime: taskPlannedEnd(task).toISOString(),
    };
}

/**
 * The status for `code`: the one recorded on the entry, else the one the notification's own taskSet
 * reports. Recorded first, because every load writes the tasks' windows onto the recorded statuses
 * (see `reconcileLoadedStatuses`) — so the recorded status is the task plus whatever the device
 * wrote since, such as the TRAV end an OSR moves. The taskSet only answers for a code the device has
 * not recorded yet.
 *
 * Exported for `deriveStatusWorkEntries` (see the timesheet derivation), which looks up TRAV/OSR
 * exactly this way.
 *
 * @param entry
 * @param code
 */
export function statusForCode(entry: NotificationState, code: string): NotificationStatus | undefined {
    const recorded = entry.status.find((status) => status.statusCode === code);
    return recorded ? statusRecordToStatus(recorded) : statusFromTaskSet(entry, code);
}

/**
 * The backend-provided status code — DISP (dispatched) already substituted for a notification the
 * backend sent none for, see `NotificationState.backendStatusCode`.
 *
 * @param entry
 */
export function backendStatusCodeForEntry(entry: NotificationState): NOTIFICATION_STATUS_CODE {
    return entry.backendStatusCode;
}

/**
 * The backend-provided status as a status record. The backend sends no window with it, so both ends
 * are stamped "now".
 *
 * @param entry
 */
function backendStatusForEntry(entry: NotificationState): NotificationStatus {
    const now = moment().toISOString();
    return { statusCode: backendStatusCodeForEntry(entry), startTime: now, endTime: now };
}

// The order statuses progress in, from dispatch (DISP) to confirmation (CRM). Kept as a plain
// lookup, mirroring `STATUS_ORDER` on `StatusModel`, so the selector layer needs no class import.
const STATUS_ORDER_LOOKUP: Record<string, number> = {
    DISP: 0,
    ETA: 1,
    DURA: 2,
    REMT: 3,
    ROSR: 4,
    FCC: 5,
    TRAV: 6,
    ATS: 7,
    OSR: 8,
    SOSR: 9,
    TOBS: 10,
    RTOBS: 11,
    FTOBS: 12,
    AVR1: 13,
    AVR2: 14,
    AVRF: 15,
    CANC: 16,
    CNCL: 17,
    CRM: 18,
};

function compareStatusOrder(a: string, b: string): number {
    const aOrder = STATUS_ORDER_LOOKUP[a];
    const bOrder = STATUS_ORDER_LOOKUP[b];
    if (typeof aOrder === 'undefined') {
        return 1;
    }
    if (typeof bOrder === 'undefined') {
        return -1;
    }
    return aOrder - bOrder;
}

/**
 * The furthest status recorded on the entry, or `undefined` when it has none yet. "Furthest" goes
 * by {@link STATUS_ORDER_LOOKUP} rather than by timestamp, since statuses only ever advance.
 *
 * @param entry
 */
function latestStatusRecord(entry: NotificationState): NotificationStatusRecordState | undefined {
    if (entry.status.length === 0) {
        return undefined;
    }
    return [...entry.status].sort((a, b) => compareStatusOrder(a.statusCode, b.statusCode)).at(-1);
}

/**
 * The status to treat as current: the furthest status recorded on the entry, falling back to the
 * backend status while none has been recorded.
 *
 * @param entry
 */
export function effectiveStatusForEntry(entry: NotificationState): NotificationStatus {
    const latest = latestStatusRecord(entry);
    return latest ? statusRecordToStatus(latest) : backendStatusForEntry(entry);
}

/**
 * The window the notification is planned for: the DURA (duration) window once an arrival was
 * announced — the device keeps one whether or not the backend creates a DURA task, see
 * `notification-status.rules.ts` — else the DISP window. `undefined` when it has neither, which a
 * well-formed notification never gets to.
 *
 * An ETA without a DURA is derived into one by the same rule here, for the entries no load has
 * reconciled yet — restored from a cache written before the device kept DURA, for instance.
 *
 * @param entry
 */
function plannedWindowForEntry(entry: NotificationState): NotificationStatus | undefined {
    const duration = statusForCode(entry, NOTIFICATION_STATUS_CODE.DURA);
    if (duration) {
        return duration;
    }
    const arrival = statusForCode(entry, NOTIFICATION_STATUS_CODE.ETA);
    if (arrival) {
        return statusRecordToStatus(durationRecordForArrival(entry, arrival.endTime));
    }
    return statusForCode(entry, NOTIFICATION_STATUS_CODE.DISP);
}

/**
 * When the notification starts: the start of {@link plannedWindowForEntry}, else the plan date the
 * backend sent.
 *
 * @param entry
 */
export function notificationStartForEntry(entry: NotificationState): moment.Moment {
    const plannedWindow = plannedWindowForEntry(entry);
    if (plannedWindow) {
        return moment(plannedWindow.startTime);
    }
    // Reaching this point means the entry has neither a DURA nor a DISP, which a well-formed
    // notification always has. A pure selector has no side-effect channel to report it through, so
    // `NotificationEffects.reportMissingStartEndStatus$` re-derives the same condition via
    // `selectEntryIdsMissingStartEndStatus` below and logs it there instead.
    return TimeUtilities.momentFromDateAndTime(entry.plannedStartDate, entry.plannedStartTime);
}

/**
 * When the notification ends: the end of {@link plannedWindowForEntry}, else the plan date the
 * backend sent.
 *
 * @param entry
 */
function notificationEndForEntry(entry: NotificationState): moment.Moment {
    const plannedWindow = plannedWindowForEntry(entry);
    if (plannedWindow) {
        return moment(plannedWindow.endTime);
    }
    // Same "nothing found" case as in `notificationStartForEntry`, reported by the same effect.
    return TimeUtilities.momentFromDateAndTime(entry.plannedEndDate, entry.plannedEndTime);
}

/**
 * `true` when the entry has no {@link plannedWindowForEntry}, so that
 * {@link notificationStartForEntry} / {@link notificationEndForEntry} have to fall back to the
 * planned start/end the backend sent. A well-formed notification never gets here.
 *
 * @param entry
 */
function hasNoStartEndStatus(entry: NotificationState): boolean {
    return plannedWindowForEntry(entry) === undefined;
}

/**
 * `true` when `today` (in local time) is on/before `plannedStartDate`, normalizing a UTC moment
 * to local first. Fixes SHCNGMAAP2-742: `moment` granularity comparisons on a UTC moment compare
 * against the wrong local midnight (e.g. JST 09:00 vs. JST 00:00) unless converted to local before
 * `startOf('day')`.
 *
 * @param plannedStart
 * @param nowMs
 */
function isSameOrBeforeToday(plannedStart: moment.Moment, nowMs: number): boolean {
    let plannedStartDate = plannedStart.clone();
    if (plannedStart.isUTC()) {
        plannedStartDate = plannedStartDate.local().startOf('day');
    }
    return plannedStartDate.isSameOrBefore(moment(nowMs).startOf('day'), 'day');
}

function isAfterToday(plannedStart: moment.Moment, nowMs: number): boolean {
    let plannedStartDate = plannedStart.clone();
    if (plannedStart.isUTC()) {
        plannedStartDate = plannedStartDate.local().startOf('day');
    }
    return plannedStartDate.isAfter(moment(nowMs).startOf('day'), 'day');
}

/**
 * `true` for an entry the user has already started: being worked on, or waiting for its signature.
 * Such an entry belongs in today's column whatever its planned start says — it is what the user is
 * doing now, and no plan date can move it out from under them. The date only decides the column for
 * the entries nobody has touched yet, which is why both {@link isTodaysEntry} and
 * {@link isUpcomingEntry} check this first and stay each other's complement.
 *
 * @param entry
 */
function isStartedEntry(entry: NotificationState): boolean {
    return isInProgressOrToBeSignedCode(effectiveStatusForEntry(entry).statusCode);
}

/**
 * `true` for the codes that keep an entry out of the today and upcoming columns: still dispatched
 * (DISP, the new column's), or its cancellation confirmed (CNCL). Whether the confirmation was
 * submitted is deliberately not read off the status — see {@link selectDashboardEntries}.
 *
 * @param code
 */
function isClosedOrDispatchedCode(code: string): boolean {
    return code === NOTIFICATION_STATUS_CODE.CNCL || code === NOTIFICATION_STATUS_CODE.DISP;
}

/**
 * Whether the notification's confirmation has been submitted, as far as the dashboard is concerned:
 * this device holds a completion record for it, or — as a fallback, for a confirmation whose record
 * has not reached this device, e.g. one submitted on another device — the backend reports it as CRM.
 *
 * The effective status is deliberately not asked: a CRM this device recorded always comes with a
 * completion record, and anything short of CRM says nothing, since it lags behind a submit still in
 * the offline queue and can stay short of CRM for good when the confirmation failed to process in
 * the backend — both would put a submitted notification back on the dashboard, to be processed a
 * second time.
 *
 * @param entry
 * @param completions the completion map
 */
function isSubmittedEntry(entry: NotificationState, completions: NotificationCompletionsState): boolean {
    return (
        completionKeyOf(entry.notificationId) in completions ||
        backendStatusCodeForEntry(entry) === NOTIFICATION_STATUS_CODE.CRM
    );
}

function isTodaysEntry(entry: NotificationState, nowMs: number): boolean {
    if (isClosedOrDispatchedCode(effectiveStatusForEntry(entry).statusCode)) {
        return false;
    }
    return isStartedEntry(entry) || isSameOrBeforeToday(notificationStartForEntry(entry), nowMs);
}

function isUpcomingEntry(entry: NotificationState, nowMs: number): boolean {
    if (isClosedOrDispatchedCode(effectiveStatusForEntry(entry).statusCode)) {
        return false;
    }
    return !isStartedEntry(entry) && isAfterToday(notificationStartForEntry(entry), nowMs);
}

function isMultiDayEntry(entry: NotificationState): boolean {
    const start = notificationStartForEntry(entry).clone().startOf('day');
    const end = notificationEndForEntry(entry).clone().startOf('day');
    return end.diff(start) > 0;
}

function mainEquipmentForEntry(entry: NotificationState): Equipment | undefined {
    const main = mainEquipmentOf(entry);
    return main ? equipmentToState(main) : undefined;
}

function affectedEquipmentForEntry(entry: NotificationState): Equipment | undefined {
    const affected = affectedEquipmentOf(entry);
    return affected ? equipmentToState(affected) : undefined;
}

/**
 * Whether the notification still counts as unread. Only a dispatched (DISP) notification can be:
 * past that the user has evidently seen it. The stored flag decides the rest, and defaults to
 * unread for a notification nothing has been recorded for.
 *
 * @param entry
 */
function unreadIndicatorForEntry(entry: NotificationState): boolean {
    if (effectiveStatusForEntry(entry).statusCode !== NOTIFICATION_STATUS_CODE.DISP) {
        return false;
    }
    return entry.unread;
}

// ---- feature selector & raw fields --------------------------------------------------------

export const selectNotificationFeature = createFeatureSelector<NotificationCollectionState>(NOTIFICATION_FEATURE_KEY);

/** The notifications the device currently has: the entries the last load delivered. */
export const selectNotificationEntries: MemoizedSelector<object, NotificationState[]> = createSelector(
    selectNotificationFeature,
    (state) => state.notifications,
);

/**
 * The completion record of every completed confirmation, keyed by `completionKeyOf` — including
 * the ones whose notification the backend no longer sends.
 */
export const selectCompletions: MemoizedSelector<object, NotificationCompletionsState> = createSelector(
    selectNotificationFeature,
    (state) => state.completions,
);

export const selectNotificationLoadStatus: MemoizedSelector<object, LoadStatus> = createSelector(
    selectNotificationFeature,
    (state) => state.loadStatus,
);

/** `true` once a backend fetch has completed. */
export const selectNotificationLoaded: MemoizedSelector<object, boolean> = createSelector(
    selectNotificationLoadStatus,
    (loadStatus) => loadStatus === LoadStatus.Loaded,
);

/**
 * What went wrong the last time the slice was filled or written, `undefined` when nothing has —
 * see `NotificationCollectionState.error`.
 */
export const selectNotificationError: MemoizedSelector<object, string | undefined> = createSelector(
    selectNotificationFeature,
    (state) => state.error,
);

export const selectIncorrectNotificationIds: MemoizedSelector<object, string[]> = createSelector(
    selectNotificationFeature,
    (state) => state.incorrectNotificationIds,
);

/** The load the entry collection reflects — see `NotificationCollectionState.lastLoad`. */
export const selectLastNotificationLoad: MemoizedSelector<object, NotificationLoadMarker | undefined> = createSelector(
    selectNotificationFeature,
    (state) => state.lastLoad,
);

/**
 * Whether notifications have ever become available for reading — the guard `NotificationNgrxService`
 * fails reads on before the slice can serve them.
 *
 * Derived rather than stored, from the two monotone facts that make the slice readable: a backend
 * load has completed (`lastLoad`, set once per load and never cleared, so a re-fetch or a failed
 * refresh cannot take readability away again), or entries are present at all — which covers the
 * offline boot, where hydration restores a warm cache before any fetch has run.
 *
 * Deliberately not `loadStatus === Loaded`: that drops back to `Loading` on every re-fetch, which
 * would make reads throw again mid-refresh for a technician who has no notifications assigned.
 */
export const selectNotificationInitialized: MemoizedSelector<object, boolean> = createSelector(
    selectLastNotificationLoad,
    selectNotificationEntries,
    (lastLoad, entries) => lastLoad !== undefined || entries.length > 0,
);

/**
 * The sequence number of the last completed load, `undefined` before the first one. Changes once
 * per load, the app-start load included, so consumers can react to "notifications were fetched" by
 * watching a value instead of subscribing to an event.
 */
export const selectNotificationsLoadedSequence: MemoizedSelector<object, number | undefined> = createSelector(
    selectLastNotificationLoad,
    (lastLoad) => lastLoad?.sequence,
);

/**
 * As {@link selectNotificationsLoadedSequence}, except that the app-start load leaves it
 * `undefined`. For consumers that react to notifications *changing* and must not fire on the
 * initial fetch, where there is nothing to refresh yet.
 */
export const selectNotificationsUpdatedSequence: MemoizedSelector<object, number | undefined> = createSelector(
    selectLastNotificationLoad,
    (lastLoad) => (lastLoad && !lastLoad.isAppStart ? lastLoad.sequence : undefined),
);

/**
 * The number of cancellations the user confirmed on this device, `undefined` before the first one.
 * The counterpart of {@link selectNotificationsUpdatedSequence} for the one list change no load
 * reports — see `NotificationCollectionState.lastCancellation`.
 */
export const selectNotificationsCancelledSequence: MemoizedSelector<object, number | undefined> = createSelector(
    selectNotificationFeature,
    (state) => state.lastCancellation,
);

/** The notification `notificationStatusSignal` currently tracks — see `NotificationCollectionState.currentNotificationId`'s doc comment. */
export const selectCurrentNotificationId: MemoizedSelector<object, NotificationIdState | undefined> = createSelector(
    selectNotificationFeature,
    (state) => state.currentNotificationId,
);

/**
 *
 */
export const selectCurrentNotificationStatus: MemoizedSelector<object, NotificationStatus> = createSelector(
    selectCurrentNotificationId,
    selectNotificationEntries,
    (id, entries) => {
        const entry = id ? entries.find((e) => NotificationStateUtilities.compare(e.notificationId, id)) : undefined;
        return entry ? effectiveStatusForEntry(entry) : createDefaultNotificationStatus();
    },
);

/**
 * Backs `NotificationNgrxService.notificationStatusSignal`. That signal is typed
 * `Signal<StatusModel>` for the sake of the many templates and components that read the model's
 * getters (see `NotificationDataInterface`), which makes this the one selector in the file allowed
 * to instantiate a model via {@link statusStateToModel} — the model stays confined to the public
 * interface boundary instead of leaking into every call site. No other selector here should follow
 * this precedent.
 */
export const selectCurrentNotificationStatusModel: MemoizedSelector<object, StatusModel> = createSelector(
    selectCurrentNotificationStatus,
    (status) => statusStateToModel(status),
);

/**
 * The addresses the notifications on the device are booked against: each entry's customer address,
 * by id.
 *
 * Only the customer address, not the entry's whole `addressIds` — those are the notification's
 * `AddressSet`, which is the very pool the timesheet's address picker offers and which hiding
 * exists to prune. Taking them all would leave nothing hideable at all, since every backend address
 * the picker shows comes from the last load (see `AddressState.outOfScope`).
 */
export const selectActiveNotificationCustomerAddressIds: MemoizedSelector<object, ReadonlySet<string>> = createSelector(
    selectNotificationEntries,
    (entries) => new Set(entries.map((entry) => entry.customerAddressId).filter(Boolean)),
);

/**
 * The completion record of one notification, `undefined` while it has none. Reads the completion
 * map, so it still answers for a notification the backend no longer sends.
 *
 * @param id
 */
export const selectCompletionById = (
    id: NotificationIdState,
): MemoizedSelector<object, NotificationCompletionState | undefined> =>
    createSelector(selectCompletions, (completions) => completions[completionKeyOf(id)]);

/**
 * `true` when the notification's confirmation was completed — see {@link selectCompletionById} for
 * why this outlives the notification.
 *
 * @param id
 */
export const selectNotificationCompletedById = (id: NotificationIdState): MemoizedSelector<object, boolean> =>
    createSelector(selectCompletionById(id), isDefined);

/**
 * `true` when the user has confirmed the cancellation of the given notification, `false` otherwise
 * (including for an id the collection does not hold).
 *
 * @param id
 */
export const selectCancellationConfirmedById = (id: NotificationIdState): MemoizedSelector<object, boolean> =>
    createSelector(selectNotificationEntries, (entries) =>
        entries.some(
            (entry) => NotificationStateUtilities.compare(entry.notificationId, id) && entry.cancellationConfirmed,
        ),
    );

/** Projects the slice to its serializable shape for the generic persistence layer. */
export const selectNotificationForPersistence = createSelector(
    selectNotificationEntries,
    selectCompletions,
    (entries, completions) => ({ entries, completions }),
);

// ---- per-id selectors ------------------------------------------------------------------------

/**
 * The stored entry with the given id, `undefined` when the collection does not hold it.
 *
 * @param id
 */
export const selectNotificationEntryById = (
    id: NotificationIdState,
): MemoizedSelector<object, NotificationState | undefined> =>
    createSelector(selectNotificationEntries, (entries) =>
        entries.find((e) => NotificationStateUtilities.compare(e.notificationId, id)),
    );

/**
 * The zero-based index of the entry for the given id, or `-1` when the collection does not hold it.
 *
 * @param id
 */
export const selectNotificationEntryIndexById = (id: NotificationIdState): MemoizedSelector<object, number> =>
    createSelector(selectNotificationEntries, (entries) =>
        entries.findIndex((e) => NotificationStateUtilities.compare(e.notificationId, id)),
    );

/**
 * The zero-based index of the entry for the given id, or — when the collection does not hold it —
 * the index it would occupy once appended. Never `-1`, so callers that treat an unknown id as a
 * new one get a usable position either way.
 *
 * @param id
 */
export const selectNotificationEntryIndexOrNextForId = (id: NotificationIdState): MemoizedSelector<object, number> =>
    createSelector(selectNotificationEntries, (entries) => {
        const index = entries.findIndex((e) => NotificationStateUtilities.compare(e.notificationId, id));
        return index === -1 ? entries.length : index;
    });

/**
 * The addresses a notification links to, resolved against the address slice, or `undefined` when
 * the collection does not hold the notification. What a caller that has to build a
 * `NotificationModel` needs besides the entry — see `entryToNotificationModel`.
 *
 * @param id
 */
export const selectNotificationAddressesById = (
    id: NotificationIdState,
): MemoizedSelector<object, NotificationAddresses | undefined> =>
    createSelector(selectNotificationEntryById(id), selectAddresses, (entry, addresses) =>
        entry ? resolveNotificationAddresses(entry, addresses) : undefined,
    );

/**
 * The notification's current status — see {@link effectiveStatusForEntry}.
 *
 * @param id
 */
export const selectEffectiveStatusById = (
    id: NotificationIdState,
): MemoizedSelector<object, NotificationStatus | undefined> =>
    createSelector(selectNotificationEntryById(id), (entry) => (entry ? effectiveStatusForEntry(entry) : undefined));

/**
 * The furthest status recorded on the notification, or `undefined` when it has none yet — unlike
 * {@link selectEffectiveStatusById}, which falls back to the backend's status.
 *
 * @param id
 */
export const selectLatestStatusRecordForId = (
    id: NotificationIdState,
): MemoizedSelector<object, NotificationStatusRecordState | undefined> =>
    createSelector(selectNotificationEntryById(id), (entry) => (entry ? latestStatusRecord(entry) : undefined));

/**
 * The status record the notification carries for `code`, or `undefined` when it has none. Straight
 * from the recorded statuses: no taskSet fallback, unlike {@link selectStatusForCodeById}.
 *
 * @param id
 * @param code
 */
export const selectStatusRecordByCodeForId = (
    id: NotificationIdState,
    code: string,
): MemoizedSelector<object, NotificationStatusRecordState | undefined> =>
    createSelector(selectNotificationEntryById(id), (entry) =>
        entry?.status.find((status) => status.statusCode === code),
    );

/**
 * `true` when the notification already carries a status with the given `code`.
 *
 * @param id
 * @param code
 */
export const selectStatusExistsForId = (id: NotificationIdState, code: string): MemoizedSelector<object, boolean> =>
    createSelector(selectStatusRecordByCodeForId(id, code), (status) => status !== undefined);

/**
 * `true` when the notification may be escalated, which every status except DISP allows: escalating
 * one the user has not accepted yet makes no sense.
 *
 * @param id
 */
export const selectCanEscalateById = (id: NotificationIdState): MemoizedSelector<object, boolean> =>
    createSelector(selectEffectiveStatusById(id), (status) => status?.statusCode !== NOTIFICATION_STATUS_CODE.DISP);

/**
 * Whether the notification has been escalated on this device, `false` when the collection does not
 * hold it.
 *
 * @param id
 */
export const selectEscalatedById = (id: NotificationIdState): MemoizedSelector<object, boolean> =>
    createSelector(selectNotificationEntryById(id), (entry) => entry?.escalated ?? false);

/**
 * The status code the notification was cancelled with, an empty string when it was not cancelled or
 * the collection does not hold it.
 *
 * @param id
 */
export const selectCancelledById = (id: NotificationIdState): MemoizedSelector<object, string> =>
    createSelector(selectNotificationEntryById(id), (entry) => entry?.cancelled ?? '');

/**
 * Whether the notification is unread — see {@link unreadIndicatorForEntry}.
 *
 * @param id
 */
export const selectUnreadIndicatorById = (id: NotificationIdState): MemoizedSelector<object, boolean> =>
    createSelector(selectNotificationEntryById(id), (entry) => (entry ? unreadIndicatorForEntry(entry) : false));

export const selectNotificationStartById = (
    id: NotificationIdState,
): MemoizedSelector<object, moment.Moment | undefined> =>
    createSelector(selectNotificationEntryById(id), (entry) => (entry ? notificationStartForEntry(entry) : undefined));

export const selectNotificationEndById = (
    id: NotificationIdState,
): MemoizedSelector<object, moment.Moment | undefined> =>
    createSelector(selectNotificationEntryById(id), (entry) => (entry ? notificationEndForEntry(entry) : undefined));

/**
 * The notification's status for `code`, the taskSet answering for a code it has not recorded — see
 * {@link statusForCode}.
 *
 * @param id
 * @param code
 */
export const selectStatusForCodeById = (
    id: NotificationIdState,
    code: NOTIFICATION_STATUS_CODE,
): MemoizedSelector<object, NotificationStatus | undefined> =>
    createSelector(selectNotificationEntryById(id), (entry) => (entry ? statusForCode(entry, code) : undefined));

/**
 * The raw task carrying the given code, straight out of the notification's taskSet. The *first*
 * match, in contrast to the status lookups above, which take the latest one.
 *
 * @param id
 * @param code
 */
export const selectTaskForCodeById = (
    id: NotificationIdState,
    code: string,
): MemoizedSelector<object, NotificationTaskState | undefined> =>
    createSelector(selectNotificationEntryById(id), (entry) => entry?.tasks.find((task) => task.statusCode === code));

export const selectIsTodaysEntry = (id: NotificationIdState, nowMs: number): MemoizedSelector<object, boolean> =>
    createSelector(selectNotificationEntryById(id), selectCompletions, (entry, completions) =>
        entry && !isSubmittedEntry(entry, completions) ? isTodaysEntry(entry, nowMs) : false,
    );

export const selectIsMultiDay = (id: NotificationIdState): MemoizedSelector<object, boolean> =>
    createSelector(selectNotificationEntryById(id), (entry) => (entry ? isMultiDayEntry(entry) : false));

/**
 * `true` when some notification other than this one is still dispatched (DISP) and not cancelled.
 *
 * @param id
 */
export const selectAnyOtherEntryInStatusDisp = (id: NotificationIdState): MemoizedSelector<object, boolean> =>
    createSelector(selectNotificationEntries, (entries) =>
        entries.some(
            (entry) =>
                !NotificationStateUtilities.compare(entry.notificationId, id) &&
                effectiveStatusForEntry(entry).statusCode === NOTIFICATION_STATUS_CODE.DISP &&
                !entry.cancelled,
        ),
    );

/**
 * `true` when the installation paperwork has to be shown: an installation notification carrying at
 * least one paperwork entry the backend marked as required.
 *
 * @param id
 */
export const selectInstallationPaperworkRequired = (id: NotificationIdState): MemoizedSelector<object, boolean> =>
    createSelector(selectNotificationEntryById(id), (entry) => {
        if (!entry) {
            return false;
        }
        return (
            entry.notificationType === ConfigConstants.NotificationTypes.Installation &&
            entry.paperwork.length > 0 &&
            entry.paperwork.some((question) => question.required)
        );
    });

export const selectNextUpcomingEntry = (
    id: NotificationIdState,
): MemoizedSelector<object, NotificationState | undefined> =>
    createSelector(selectNotificationEntries, (entries) => {
        const mainEntry = entries.find((e) => NotificationStateUtilities.compare(e.notificationId, id));
        if (!mainEntry) {
            return undefined;
        }
        const mainStart = notificationStartForEntry(mainEntry);
        return entries.find((entry) => notificationStartForEntry(entry).isAfter(mainStart));
    });

export const selectMainEquipmentForId = (id: NotificationIdState): MemoizedSelector<object, Equipment | undefined> =>
    createSelector(selectNotificationEntryById(id), (entry) => (entry ? mainEquipmentForEntry(entry) : undefined));

export const selectEntriesByEquipment = (equipmentId: string): MemoizedSelector<object, NotificationState[]> =>
    createSelector(selectNotificationEntries, (entries) =>
        entries.filter((entry) => mainEquipmentForEntry(entry)?.equnr === equipmentId),
    );

/**
 * The equipment's entries, each already paired with its current status, so a consumer needs one
 * store read instead of one per entry.
 *
 * @param equipmentId
 */
export const selectEntryStatusPairsByEquipment = (equipmentId: string): MemoizedSelector<object, EntryStatusPair[]> =>
    createSelector(selectEntriesByEquipment(equipmentId), (entries) =>
        entries.map((entry) => ({ entry, status: effectiveStatusForEntry(entry) })),
    );

// ---- bulk selectors --------------------------------------------------------------------------

/**
 * Entries ordered by {@link notificationStartForEntry} — the start the dashboard card shows. The
 * reducer sorts by the same start, but only when the collection is loaded or restored, so the
 * status writes in between (an ETA moving the start) are not reflected in its order. A plain function
 * rather than a `MemoizedSelector`, as the statuses sit on the entry the caller already holds.
 *
 * @param entries
 */
export function selectEntriesSortedByStartDate(entries: NotificationState[]): NotificationState[] {
    return [...entries].sort((a, b) => notificationStartForEntry(a).valueOf() - notificationStartForEntry(b).valueOf());
}

/**
 * Entries with the started ones — in progress or awaiting their signature, see
 * {@link isStartedEntry} — pulled to the front, the order among each group left as it came in. A
 * plain function for the same reason as {@link selectEntriesSortedByStartDate}: the status now sits
 * on the entry the caller already holds.
 *
 * @param entries
 */
export function selectEntriesSortedByStatus(entries: NotificationState[]): NotificationState[] {
    return [...entries].sort((a, b) => Number(isStartedEntry(b)) - Number(isStartedEntry(a)));
}

/**
 * The entries the dashboard's columns choose from: every notification whose confirmation has not
 * been submitted (see {@link isSubmittedEntry}).
 */
export const selectDashboardEntries: MemoizedSelector<object, NotificationState[]> = createSelector(
    selectNotificationEntries,
    selectCompletions,
    (entries, completions) => entries.filter((entry) => !isSubmittedEntry(entry, completions)),
);

/**
 * The dashboard's "today" column: entries that start today or earlier — plus every entry the user
 * has already started, however far out it was planned (see {@link isStartedEntry}) — and are
 * neither still dispatched, nor submitted (see {@link selectDashboardEntries}), nor cancelled with
 * the cancellation confirmed, with the started ones first and each group ordered by start.
 *
 * @param nowMs
 */
export const selectTodaysEntries = (nowMs: number): MemoizedSelector<object, NotificationState[]> =>
    createSelector(selectDashboardEntries, (entries) =>
        selectEntriesSortedByStatus(
            selectEntriesSortedByStartDate(entries.filter((entry) => isTodaysEntry(entry, nowMs))),
        ),
    );

/**
 * The dashboard's "upcoming" column: entries that start after today, have not been started yet
 * (see {@link isStartedEntry}) and are neither still dispatched, nor submitted (see
 * {@link selectDashboardEntries}), nor cancelled with the cancellation confirmed, ordered by start.
 *
 * @param nowMs
 */
export const selectUpcomingEntries = (nowMs: number): MemoizedSelector<object, NotificationState[]> =>
    createSelector(selectDashboardEntries, (entries) =>
        selectEntriesSortedByStartDate(entries.filter((entry) => isUpcomingEntry(entry, nowMs))),
    );

/**
 * The dashboard's "new" column: entries the user has not accepted yet and whose confirmation was not
 * submitted (see {@link selectDashboardEntries}), sorted by start date.
 */
export const selectNewEntries: MemoizedSelector<object, NotificationState[]> = createSelector(
    selectDashboardEntries,
    (entries) =>
        entries
            .filter((entry) => effectiveStatusForEntry(entry).statusCode === NOTIFICATION_STATUS_CODE.DISP)
            .sort((a, b) => notificationStartForEntry(a).valueOf() - notificationStartForEntry(b).valueOf()),
);

/**
 * Every entry currently being worked on.
 *
 * A notification whose confirmation was submitted is left out even when its status still reads as in
 * progress: the backend can fail to process a confirmation and keep sending the notification as OSR,
 * and the dashboard already hides such a notification (see {@link selectDashboardEntries}).
 */
export const selectEntriesInProgress: MemoizedSelector<object, NotificationState[]> = createSelector(
    selectNotificationEntries,
    selectCompletions,
    (entries, completions) =>
        entries.filter(
            (entry) =>
                !isSubmittedEntry(entry, completions) && isInProgressCode(effectiveStatusForEntry(entry).statusCode),
        ),
);

export const selectHasEntriesInProgress: MemoizedSelector<object, boolean> = createSelector(
    selectEntriesInProgress,
    (entries) => entries?.length > 0,
);

/**
 * The ids of every notification other than `id` that is currently being worked on — for the
 * travel-status-update pipeline's "other notifications in progress" modal.
 *
 * The legacy `!notification.isPaused` term is dropped: `isPaused` is hard-coded to `false` in the
 * state->model bridge (paused notifications are no longer supported), so that filter could never
 * exclude anything.
 *
 * @param id the notification being submitted, excluded from the result
 */
export const selectOtherEntriesInProgress = (
    id: NotificationIdState,
): MemoizedSelector<object, NotificationIdState[]> =>
    createSelector(selectEntriesInProgress, (entries) =>
        entries
            .filter((entry) => !NotificationStateUtilities.compare(entry.notificationId, id))
            .map((entry) => entry.notificationId),
    );

/** The first entry currently being worked on, in collection order. */
export const selectFirstEntryInProgress: MemoizedSelector<object, NotificationState | undefined> = createSelector(
    selectEntriesInProgress,
    (entries) => entries[0],
);

/**
 * The entry to treat as *the* one being worked on: being on site (OSR) outranks travelling to it or
 * working it remotely (TRAV/ROSR/FCC).
 *
 * @param entries the entries to pick from, already narrowed to the in-progress ones
 */
export function firstInProgressByPriority(entries: readonly NotificationState[]): NotificationState | undefined {
    const osr = entries.find((entry) => effectiveStatusForEntry(entry).statusCode === NOTIFICATION_STATUS_CODE.OSR);
    if (osr) {
        return osr;
    }
    return entries.find((entry) => {
        const code = effectiveStatusForEntry(entry).statusCode;
        return (
            code === NOTIFICATION_STATUS_CODE.TRAV ||
            code === NOTIFICATION_STATUS_CODE.ROSR ||
            code === NOTIFICATION_STATUS_CODE.FCC
        );
    });
}

/** Every entry whose service report is waiting for the customer's signature. */
export const selectEntriesToBeSigned: MemoizedSelector<object, NotificationState[]> = createSelector(
    selectNotificationEntries,
    (entries) => entries.filter((entry) => isToBeSignedCode(effectiveStatusForEntry(entry).statusCode)),
);

export const selectHasEntriesToBeSigned: MemoizedSelector<object, boolean> = createSelector(
    selectEntriesToBeSigned,
    (entries) => entries?.length > 0,
);

export const selectFirstEntryToBeSigned: MemoizedSelector<object, NotificationState | undefined> = createSelector(
    selectEntriesToBeSigned,
    (entries) => entries[0],
);

/**
 * The first entry that is being worked on or waiting for its signature, or `undefined` when there
 * is none. Unlike {@link selectEntriesInProgress} and {@link selectEntriesToBeSigned}, this one
 * ignores the backend status and looks only at what was recorded on the device, since its callers
 * ask "did the user start something here?".
 */
export const selectInProgressOrToBeSignedEntry: MemoizedSelector<object, NotificationState | undefined> =
    createSelector(selectNotificationEntries, (entries) =>
        entries.find((entry) => {
            const latest = latestStatusRecord(entry);
            return latest ? isInProgressOrToBeSignedCode(latest.statusCode) : false;
        }),
    );

export const selectHasEntriesInProgressOrToBeSigned: MemoizedSelector<object, boolean> = createSelector(
    selectHasEntriesInProgress,
    selectHasEntriesToBeSigned,
    (hasEntriesInProgress, hasEntriesToBeSigned) => hasEntriesInProgress || hasEntriesToBeSigned,
);

/** DISP is the only status lower than ETA. */
export const selectEntriesInEtaOrHigher: MemoizedSelector<object, NotificationState[]> = createSelector(
    selectNotificationEntries,
    (entries) => entries.filter((entry) => effectiveStatusForEntry(entry).statusCode !== NOTIFICATION_STATUS_CODE.DISP),
);

/** Every entry the user has announced an arrival time (ETA) for. */
export const selectEntriesInEtaStatus: MemoizedSelector<object, NotificationState[]> = createSelector(
    selectNotificationEntries,
    (entries) => entries.filter((entry) => effectiveStatusForEntry(entry).statusCode === NOTIFICATION_STATUS_CODE.ETA),
);

/** Every entry that has reached on-site (OSR) or any later status. */
export const selectEntriesInOsrOrHigher: MemoizedSelector<object, NotificationState[]> = createSelector(
    selectNotificationEntries,
    (entries) => entries.filter((entry) => OSR_OR_HIGHER_CODES.has(effectiveStatusForEntry(entry).statusCode)),
);

/**
 * {@link selectEntriesInOsrOrHigher} with each entry's current status already paired to it, the same
 * way {@link selectEntryStatusPairsByEquipment} does for one equipment.
 */
export const selectEntryStatusPairsInOsrOrHigher: MemoizedSelector<object, EntryStatusPair[]> = createSelector(
    selectEntriesInOsrOrHigher,
    (entries) => entries.map((entry) => ({ entry, status: effectiveStatusForEntry(entry) })),
);

/**
 * The customer address of every notification, used by the material-return screen. Resolved against
 * the address slice, which owns the addresses the notification only links to — a link that does not
 * resolve is skipped rather than filled with a blank address, so a caller never mistakes an empty
 * address for the customer's.
 */
export const selectCustomerAddressStates: MemoizedSelector<object, AddressState[]> = createSelector(
    selectNotificationEntries,
    selectAddresses,
    (entries, addresses) =>
        entries.map((entry) => addresses.find((address) => address.id === entry.customerAddressId)).filter(isDefined),
);

/** The main equipment of every notification, skipping the ones the backend sent none for. */
export const selectEquipmentStates: MemoizedSelector<object, Equipment[]> = createSelector(
    selectNotificationEntries,
    (entries) => entries.map(mainEquipmentForEntry).filter(isDefined),
);

/**
 * Every distinct service-part-questionnaire document id across the notifications that are not
 * confirmed yet — the documents still worth refreshing.
 */
export const selectAllSpqDocIds: MemoizedSelector<object, string[]> = createSelector(
    selectNotificationEntries,
    (entries) => [
        ...new Set(
            entries
                .filter((entry) => effectiveStatusForEntry(entry).statusCode !== NOTIFICATION_STATUS_CODE.CRM)
                .flatMap((entry) => entry.parts.map((part) => part.spqDocumentId))
                .filter((docId) => isDefined(docId) && docId !== ''),
        ),
    ],
);

/** The ids the backend sent that could not be parsed, which an effect surfaces to the user. */
export const selectNotificationsNotLoaded: MemoizedSelector<object, string[]> = selectIncorrectNotificationIds;

/**
 * The ids for which neither a DURA nor an ETA/DISP task/status could be found, so that their start
 * and end fall back to the raw `PlanStartDate`/`PlanEndDate`. Backs
 * `NotificationEffects.reportMissingStartEndStatus$`, which logs them from a `dispatch: false`
 * effect — a pure selector cannot, see {@link notificationStartForEntry}.
 */
export const selectEntryIdsMissingStartEndStatus: MemoizedSelector<object, NotificationIdState[]> = createSelector(
    selectNotificationEntries,
    (entries) => entries.filter(hasNoStartEndStatus).map((entry) => entry.notificationId),
);

// ---- the next-status matrix and availability --------------------------------------------------

function sharedStatuses(): Partial<Record<string, NextStatusEntry[]>> {
    const crmAndScrm: NextStatusEntry[] = [
        { statusCode: NOTIFICATION_STATUS_CODE.CRM, enabled: true },
        { statusCode: NOTIFICATION_STATUS_CODE.SCRM, enabled: true },
    ];
    return {
        ROSR: crmAndScrm,
        FCC: crmAndScrm,
        OSR: crmAndScrm,
        TOBS: crmAndScrm,
        RTOBS: crmAndScrm,
        FTOBS: crmAndScrm,
        CRM: [],
    };
}

function conditionalStatuses(enableSAF: boolean): Partial<Record<string, NextStatusEntry[]>> {
    const enabled = (statusCode: NOTIFICATION_STATUS_CODE): NextStatusEntry => ({ statusCode, enabled: true });
    const statuses: Partial<Record<string, NextStatusEntry[]>> = {
        DISP: enableSAF
            ? [
                  enabled(NOTIFICATION_STATUS_CODE.ETA),
                  enabled(NOTIFICATION_STATUS_CODE.FCC),
                  enabled(NOTIFICATION_STATUS_CODE.TRAV),
                  enabled(NOTIFICATION_STATUS_CODE.ROSR),
              ]
            : [
                  enabled(NOTIFICATION_STATUS_CODE.ETA),
                  enabled(NOTIFICATION_STATUS_CODE.FCC),
                  enabled(NOTIFICATION_STATUS_CODE.TRAV),
                  enabled(NOTIFICATION_STATUS_CODE.OSR),
                  enabled(NOTIFICATION_STATUS_CODE.ROSR),
              ],
        ETA: enableSAF
            ? [
                  enabled(NOTIFICATION_STATUS_CODE.REMT),
                  enabled(NOTIFICATION_STATUS_CODE.ROSR),
                  enabled(NOTIFICATION_STATUS_CODE.FCC),
                  enabled(NOTIFICATION_STATUS_CODE.TRAV),
              ]
            : [
                  enabled(NOTIFICATION_STATUS_CODE.REMT),
                  enabled(NOTIFICATION_STATUS_CODE.ROSR),
                  enabled(NOTIFICATION_STATUS_CODE.FCC),
                  enabled(NOTIFICATION_STATUS_CODE.TRAV),
                  enabled(NOTIFICATION_STATUS_CODE.OSR),
              ],
        TRAV: enableSAF
            ? [enabled(NOTIFICATION_STATUS_CODE.ATS), enabled(NOTIFICATION_STATUS_CODE.OSR)]
            : [enabled(NOTIFICATION_STATUS_CODE.OSR)],
    };
    statuses[NOTIFICATION_STATUS_CODE.DURA] = [...(statuses[NOTIFICATION_STATUS_CODE.ETA] ?? [])];
    if (enableSAF) {
        statuses[NOTIFICATION_STATUS_CODE.ATS] = [enabled(NOTIFICATION_STATUS_CODE.OSR)];
    }
    return statuses;
}

/**
 * Which statuses a notification may move to next, keyed by the status it is in now. Derived per
 * read, so every consumer gets its own matrix: flipping an entry's `enabled` — as
 * {@link selectAvailableStatusForId} does — cannot leak into the next reader.
 *
 * Composes the config slice's `selectEnableSafetyAssessmentForm` directly rather than a snapshot
 * copied into this slice: the config domain is migrated, so the flag has exactly one home and
 * cannot go stale here — the same reasoning applies to `selectAvailableStatusForId`'s
 */
export const selectNextStatusMatrix: MemoizedSelector<
    object,
    Partial<Record<string, NextStatusEntry[]>>
> = createSelector(selectEnableSafetyAssessmentForm, (enableSafetyAssessmentForm) => ({
    ...sharedStatuses(),
    ...conditionalStatuses(enableSafetyAssessmentForm),
}));

/**
 * The statuses the user may actually pick next: {@link selectNextStatusMatrix}'s row for the
 * current status, with short confirmation (SCRM) enabled only for a multi-day notification that has
 * not reached its last day, and with TRAV/OSR disabled while another notification is in progress
 * unless the config allows pausing one.
 *
 * @param id
 * @param nowMs the timestamp the multi-day check is evaluated against
 */
export const selectAvailableStatusForId = (
    id: NotificationIdState,
    nowMs: number,
): MemoizedSelector<object, NOTIFICATION_STATUS_CODE[]> =>
    createSelector(
        selectNotificationEntryById(id),
        selectNextStatusMatrix,
        selectNotificationEntries,
        (entry, matrix, allEntries) => {
            if (!entry) {
                return [];
            }
            const currentStatus = effectiveStatusForEntry(entry);
            const available = matrix[currentStatus.statusCode];
            if (!available) {
                return [];
            }
            let entries = available.map((s) => ({ ...s }));

            const isMultiDay = isMultiDayEntry(entry);
            const shortConfirmationConditionsFulfilled =
                isMultiDay && moment(nowMs).isBefore(notificationEndForEntry(entry), 'days');
            const scrmEnabled =
                shortConfirmationConditionsFulfilled &&
                (isToBeSignedCode(currentStatus.statusCode) ||
                    currentStatus.statusCode === NOTIFICATION_STATUS_CODE.OSR ||
                    isRemoteWorkCode(currentStatus.statusCode));
            entries = entries.map((s) =>
                s.statusCode === NOTIFICATION_STATUS_CODE.SCRM ? { ...s, enabled: scrmEnabled } : s,
            );

            const anyOtherInProgress = allEntries.some(
                (other) =>
                    !NotificationStateUtilities.compare(other.notificationId, id) &&
                    isInProgressCode(effectiveStatusForEntry(other).statusCode),
            );
            if (anyOtherInProgress) {
                entries = entries.map((s) =>
                    s.statusCode === NOTIFICATION_STATUS_CODE.TRAV || s.statusCode === NOTIFICATION_STATUS_CODE.OSR
                        ? { ...s, enabled: true }
                        : s,
                );
            }

            return entries.filter((s) => s.enabled).map((s) => s.statusCode);
        },
    );

/**
 * The notification's time window, status and flags in one shot, so
 * `NotificationNgrxService.setNotificationProperties` reads the store once instead of six times.
 *
 * @param id
 */
export const selectNotificationPropertiesSnapshotById = (
    id: NotificationIdState,
): MemoizedSelector<object, NotificationPropertiesSnapshot> =>
    createSelector(
        selectNotificationStartById(id),
        selectNotificationEndById(id),
        selectEffectiveStatusById(id),
        selectNotificationEntryById(id),
        selectUnreadIndicatorById(id),
        (dispOrEtaStart, dispOrEtaEnd, updatedStatus, entry, isUnread) => ({
            dispOrEtaStart,
            dispOrEtaEnd,
            updatedStatus,
            isEscalated: entry?.escalated ?? false,
            isCancelled: entry?.cancelled ?? '',
            isUnread,
        }),
    );

// ---- dashboard card projections ---------------------------------------------------------------

/**
 * The customer address of an entry, or `undefined` when the address collection does not hold it.
 *
 * @param entry
 * @param addresses the address collection to resolve against
 */
function customerAddressForEntry(
    entry: NotificationState,
    addresses: readonly AddressState[],
): AddressState | undefined {
    return addresses.find((address) => address.id === entry.customerAddressId);
}

/**
 * Projects one entry to its card state.
 *
 * @param entry the notification entry
 * @param addresses the address collection the entry's customer address is resolved against
 */
function entryToCardState(entry: NotificationState, addresses: readonly AddressState[]): NotificationCardState {
    const mainEquipment = mainEquipmentForEntry(entry);
    return {
        notificationId: entry.notificationId,
        statusCode: effectiveStatusForEntry(entry).statusCode,
        start: notificationStartForEntry(entry),
        end: notificationEndForEntry(entry),
        isMultiDay: isMultiDayEntry(entry),
        isEscalated: entry.escalated,
        isCancelled: entry.cancelled,
        isUnread: unreadIndicatorForEntry(entry),
        customerName: customerNameOf(customerAddressForEntry(entry, addresses)),
        notificationType: entry.notificationType,
        hasMainEquipment: isDefined(mainEquipment),
        mainEquipmentTidnr: mainEquipment?.tidnr ?? '',
        mainEquipmentSerialNumber: mainEquipment?.sernr ?? '',
        mainEquipmentProductName: mainEquipment?.equiShortDesc ?? '',
        affectedEquipmentName: affectedEquipmentForEntry(entry)?.equiShortDesc ?? '',
        clarifyText: entry.clarifyText,
        effect: entry.effect,
        formsCount: entry.formsCount,
        userStatus: entry.userStatus,
        taskGroup: entry.taskGroup,
        participantCount: entry.participants.length,
        materialOrderNumbers: entry.parts.map((part) => part.purchaseOrderNumber),
    };
}

/**
 * Card state of a single notification, for a consumer that holds an id rather than a column —
 * e.g. re-reading the notification behind a card the user selected.
 *
 * @param id the entry id
 */
export const selectCardStateById = (
    id: NotificationIdState,
): MemoizedSelector<object, NotificationCardState | undefined> =>
    createSelector(selectNotificationEntryById(id), selectAddresses, (entry, addresses) =>
        entry ? entryToCardState(entry, addresses) : undefined,
    );

/**
 * Whether the notification is currently being worked on.
 *
 * @param id the entry id
 */
export const selectIsInProgressById = (id: NotificationIdState): MemoizedSelector<object, boolean> =>
    createSelector(selectEffectiveStatusById(id), (status) => (status ? isInProgressCode(status.statusCode) : false));

/**
 * Card states for the entries an `entriesSelector` yields — the shared body of the three
 * dashboard-column selectors below. Exported so a consumer with its own entry subset (e.g. a
 * filtered list) can project it the same way.
 *
 * @param entriesSelector the selector yielding the entries to project
 */
export const selectCardStatesFor = (
    entriesSelector: MemoizedSelector<object, NotificationState[]>,
): MemoizedSelector<object, NotificationCardState[]> =>
    createSelector(entriesSelector, selectAddresses, (entries, addresses) =>
        entries.map((entry) => entryToCardState(entry, addresses)),
    );

/**
 * Card states for the dashboard's "today" column, in the order {@link selectTodaysEntries} yields.
 *
 * @param nowMs see {@link selectTodaysEntries}
 */
export const selectTodaysCardStates = (nowMs: number): MemoizedSelector<object, NotificationCardState[]> =>
    selectCardStatesFor(selectTodaysEntries(nowMs));

/**
 * Card states for the dashboard's "upcoming" column.
 *
 * @param nowMs see {@link selectUpcomingEntries}
 */
export const selectUpcomingCardStates = (nowMs: number): MemoizedSelector<object, NotificationCardState[]> =>
    selectCardStatesFor(selectUpcomingEntries(nowMs));

/** Card states for the dashboard's "new" column. */
export const selectNewCardStates: MemoizedSelector<object, NotificationCardState[]> =
    selectCardStatesFor(selectNewEntries);

// ---- notification-details projections ---------------------------------------------------------

/**
 * Detail state of a single notification.
 *
 * @param id the entry id
 * @param nowMs the timestamp the status availability is evaluated against — see
 *     {@link selectAvailableStatusForId}
 */
export const selectNotificationDetailById = (
    id: NotificationIdState,
    nowMs: number,
): MemoizedSelector<object, NotificationDetailState | undefined> =>
    createSelector(
        selectNotificationEntryById(id),
        selectAvailableStatusForId(id, nowMs),
        selectTaskForCodeById(id, NOTIFICATION_STATUS_CODE.DURA),
        selectAddresses,
        (entry, availableStatus, durationTask, addresses) => {
            if (!entry) {
                return undefined;
            }
            const mainEquipment = mainEquipmentOf(entry);
            return {
                notificationId: entry.notificationId,
                status: effectiveStatusForEntry(entry),
                start: notificationStartForEntry(entry),
                end: notificationEndForEntry(entry),
                isMultiDay: isMultiDayEntry(entry),
                isEscalated: entry.escalated,
                isCancelled: entry.cancelled,
                isUnread: unreadIndicatorForEntry(entry),
                customerName: customerNameOf(customerAddressForEntry(entry, addresses)),
                notificationType: entry.notificationType,
                processIndicator: entry.processIndicator,
                userStatus: entry.userStatus,
                taskGroup: entry.taskGroup,
                salesOrg: entry.salesOrg,
                functionalLocation: entry.functionalLocation,
                customerPurchaseOrderNumber: entry.customerPurchaseOrderNumber,
                clarifyText: entry.clarifyText,
                hasObjectInformation: entry.objectInformationLines.length > 0,
                hasContractSections: entry.contracts.length > 0,
                hasMainEquipment: isDefined(mainEquipment),
                mainEquipment: mainEquipment ? equipmentToState(mainEquipment) : undefined,
                mainEquipmentContractType: mainEquipment?.contractType ?? '',
                formsCount: entry.formsCount,
                participantCount: entry.participants.length,
                materialOrderNumbers: entry.parts.map((part) => part.purchaseOrderNumber),
                durationTaskNumber: durationTask?.taskNumber ?? '',
                hasDurationTask: isDefined(durationTask),
                availableStatus,
            };
        },
    );

/**
 * The notifications the details screen's previous/next arrows step through: today's, the upcoming
 * and the new ones, in that order.
 *
 * Cancelled notifications are dropped: the ones whose status code says so, and the ones the user
 * has not confirmed the cancellation of. The second check used to be the consumer's job, because the
 * confirmation was stored outside the slice; it sits on the notification now, so both halves belong
 * here.
 *
 * @param nowMs see {@link selectTodaysEntries}
 */
export const selectNotificationNavigationEntries = (
    nowMs: number,
): MemoizedSelector<object, NotificationNavigationEntry[]> =>
    createSelector(
        selectTodaysEntries(nowMs),
        selectUpcomingEntries(nowMs),
        selectNewEntries,
        (todays, upcoming, newEntries) =>
            [...todays, ...upcoming, ...newEntries]
                .filter((entry) => entry.cancelled.length === 0 || entry.cancellationConfirmed)
                .map((entry) => ({
                    notificationId: entry.notificationId,
                    statusCode: effectiveStatusForEntry(entry).statusCode,
                    isCancelled: entry.cancelled,
                }))
                .filter((entry) => !isCancelledStatusCode(entry.statusCode)),
    );
